import { auth } from '../firebase';

export type TelemetryCategory =
  | 'page_view'
  | 'feature_use'
  | 'action'
  | 'abandonment'
  | 'error'
  | 'lifecycle'
  | 'ai';

export type Primitive = string | number | boolean;

interface EventIn {
  event_name: string;
  category: TelemetryCategory;
  session_id: string;
  route?: string;
  duration_ms?: number;
  client_ts?: string;
  metadata?: Record<string, Primitive>;
}

// ---------------------------------------------------------------------------
// Session ID
// ---------------------------------------------------------------------------

const SESSION_KEY = '__octopm_telemetry_sid';
const SESSION_TS_KEY = '__octopm_telemetry_sid_ts';
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 minutes

function getSessionId(): string {
  try {
    const now = Date.now();
    const lastTs = Number(sessionStorage.getItem(SESSION_TS_KEY) ?? 0);
    const existing = sessionStorage.getItem(SESSION_KEY);

    if (existing && now - lastTs < SESSION_IDLE_MS) {
      sessionStorage.setItem(SESSION_TS_KEY, String(now));
      return existing;
    }

    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return 'unknown';
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const _buffer: EventIn[] = [];
let _currentRoute: string | undefined;
let _flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 10_000;
const BUFFER_FLUSH_SIZE = 20;

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:8000/api/v1';

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

// Cached token kept fresh via onIdTokenChanged so beaconBatch can use it
// synchronously — sendBeacon cannot set custom headers, fetch+keepalive can.
let _cachedToken: string | null = null;
auth.onIdTokenChanged(async (user) => {
  if (user) {
    try { _cachedToken = await user.getIdToken(); } catch { _cachedToken = null; }
  } else {
    _cachedToken = null;
  }
});

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

async function sendBatch(events: EventIn[]): Promise<void> {
  if (events.length === 0) return;
  if (!auth.currentUser) return;

  const url = `${BASE_URL}/telemetry/events`;
  const body = JSON.stringify({ events });

  try {
    const headers = await getAuthHeaders();
    await fetch(url, { method: 'POST', headers, body });
  } catch {
    // swallow silently
  }
}

// sendBeacon cannot send custom headers so it always 401s on our auth-gated
// endpoint. Use fetch with keepalive:true instead — it survives page unload
// and supports Authorization.
function beaconBatch(events: EventIn[]): void {
  if (events.length === 0) return;
  if (!_cachedToken) return;

  const url = `${BASE_URL}/telemetry/events`;
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${_cachedToken}` },
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // swallow
  }
}

function drainBuffer(): EventIn[] {
  const drained = _buffer.splice(0, _buffer.length);
  return drained;
}

export async function flushTelemetry(): Promise<void> {
  const events = drainBuffer();
  await sendBatch(events);
}

function scheduleFlush(): void {
  if (_flushTimer !== null) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    void flushTelemetry();
  }, FLUSH_INTERVAL_MS);
}

// ---------------------------------------------------------------------------
// Beacon on page hide / unload
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (_flushTimer !== null) {
        clearTimeout(_flushTimer);
        _flushTimer = null;
      }
      beaconBatch(drainBuffer());
    }
  });

  window.addEventListener('beforeunload', () => {
    beaconBatch(drainBuffer());
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function setTelemetryRoute(route: string): void {
  _currentRoute = route;
}

export function setTelemetryUser(
  userId: string | null,
  role: string | null,
): void {
  // Suppress unused-variable warnings; these params are reserved for future
  // per-user context enrichment (e.g. attaching role to the buffer state).
  void userId;
  void role;

  // Discard (do not send) anything queued under the previous identity. It
  // was buffered against the old user's session/token; flushing it now
  // would either fire under the new identity's token (mis-attributing the
  // event) or silently no-op once auth.currentUser has already moved on.
  // Dropping a handful of unset events is preferable to either outcome.
  if (_flushTimer !== null) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }
  _buffer.length = 0;

  // Rotate session on auth change so a new login starts a fresh session —
  // sessionStorage is per-tab and otherwise survives sign-out/sign-in,
  // which would let two different identities share one session id.
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_TS_KEY);
  } catch {
    // ignore
  }
}

export function track(
  eventName: string,
  category: TelemetryCategory,
  metadata?: Record<string, Primitive>,
): void {
  try {
    // Promote durationMs out of metadata into the top-level field so the
    // backend model and rollup worker can read it at data.get("durationMs").
    let durationMs: number | undefined;
    let cleanMeta = metadata;
    if (metadata && typeof metadata['durationMs'] === 'number') {
      durationMs = metadata['durationMs'] as number;
      cleanMeta = Object.fromEntries(
        Object.entries(metadata).filter(([k]) => k !== 'durationMs'),
      ) as Record<string, Primitive>;
    }

    const event: EventIn = {
      event_name: eventName,
      category,
      session_id: getSessionId(),
      client_ts: new Date().toISOString(),
      ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
      ...(cleanMeta && Object.keys(cleanMeta).length > 0 ? { metadata: cleanMeta } : {}),
      ...(_currentRoute !== undefined ? { route: _currentRoute } : {}),
    };

    _buffer.push(event);

    if (_buffer.length >= BUFFER_FLUSH_SIZE) {
      if (_flushTimer !== null) {
        clearTimeout(_flushTimer);
        _flushTimer = null;
      }
      void flushTelemetry();
    } else {
      scheduleFlush();
    }
  } catch {
    // swallow
  }
}
